import {
  Fragment,
  PropsWithChildren,
  RefObject,
  createContext,
  forwardRef,
  startTransition,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { css } from "@emotion/react";
import { rgba } from "polished";
import { createPortal } from "react-dom";
import { atom, PrimitiveAtom, useAtom, useAtomValue } from "jotai";
import { without } from "lodash";
import T from "../components/text/Text";
import Clickable from "../components/Clickable";
import Icon, { IconName } from "../components/Icon";
import Theme from "./Theme/client";
import { setSearchParam } from "./Routing/useSearchParam";

type TabsProps = PropsWithChildren<{
  showSingleTab?: boolean;
  queryParam?: string;
}>;

const Tabs = forwardRef<TabsRef, TabsProps>((props, ref) => {
  const { showSingleTab = false, queryParam } = props;

  const tabsAtom = useMemo(() => atom<Array<string>>([]), []);
  const currentTabAtom = useMemo(() => atom<string | null>(null), []);
  const portalRef = useRef<HTMLDivElement>(null);

  const wrapperCss = css({});

  const barCss = css({
    display: "flex",
    borderRadius: 6,
    overflow: "hidden",
    paddingLeft: 10,
    paddingRight: 10,
    overflowX: "auto",
  });

  const contentCss = css({
    padding: 20,
    borderRadius: 6,
    borderWidth: 3,
    borderStyle: "solid",
    borderColor: "white",
    position: "relative",
    ":before": {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      content: '""',
      zIndex: -1,
      backdropFilter: "blur(20px)",
    },
  });

  const tabs = useAtomValue(tabsAtom);

  const numberOfTabs = tabs.length;
  const applyStyle = (numberOfTabs === 1 && showSingleTab) || numberOfTabs > 1;

  const [currentTab, setCurrentTab] = useAtom(currentTabAtom);

  useEffect(() => {
    if (!queryParam) return;
    setSearchParam(queryParam, currentTab);
  }, [currentTab, queryParam]);

  const context: TabsContextValue = {
    tabsAtom,
    currentTabAtom,
    portalRef,
    showSingleTab,
    queryParam,
  };

  useImperativeHandle(ref, () => ({
    setCurrentTab: setCurrentTab,
  }));

  return (
    <TabsContext.Provider value={context}>
      <div css={applyStyle ? wrapperCss : null}>
        <div css={applyStyle ? barCss : null}>{props.children}</div>
        <div css={applyStyle ? contentCss : null}>
          <div ref={portalRef}></div>
        </div>
      </div>
    </TabsContext.Provider>
  );
});

export default Tabs;

type TabsTabProps = PropsWithChildren<{
  id: string;
  title: string;
  subtitle?: string;
  icon?: IconName;
}>;

export function Tab(props: TabsTabProps) {
  const { id, title, subtitle, icon } = props;

  const { showSingleTab, portalRef } = useTabsContext();

  const [tabs, setTabs] = useTabs();
  const [currentTab, setCurrentTab] = useCurrentTab();

  useEffect(() => {
    setTabs((current) => [...current, id]);
    return () => setTabs((current) => without(current, id));
  }, [id]);

  useEffect(() => {
    setCurrentTab((current) => (current === null ? id : current));
  }, [id]);
  const isOpened = currentTab === id;

  const tabsCount = tabs.length;
  const applyStyle = (tabsCount === 1 && showSingleTab) || tabsCount > 1;

  const containerCss = css({
    display: "flex",
    flexDirection: "column",
    background: isOpened ? "white" : Theme.background.loggedin,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: isOpened ? Theme.background.loggedin : "transparent",
    borderBottom: "none",
    marginLeft: 3,
    marginRight: 3,
    transition: "all 150ms",
    paddingLeft: 10,
    paddingRight: 10,
    paddingBottom: 6,
    paddingTop: 6,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    cursor: "pointer",
    "&:hover": {
      background: rgba("white", isOpened ? 1 : 0.5),
    },
  });

  const tabNode = (
    <Clickable
      css={containerCss}
      onClick={() => startTransition(() => setCurrentTab(() => id))}
    >
      <T align="center" oneLine>
        {title}
      </T>
      <T align="center" style="minor">
        {subtitle}
      </T>
      {icon && <Icon name={icon} />}
    </Clickable>
  );

  return (
    <Fragment>
      {applyStyle ? tabNode : null}
      {isOpened && portalRef.current
        ? createPortal(props.children, portalRef.current)
        : null}
    </Fragment>
  );
}

type TabsContextValue = {
  tabsAtom: PrimitiveAtom<Array<string>>;
  currentTabAtom: PrimitiveAtom<string | null>;
  showSingleTab: boolean;
  queryParam?: string;
  portalRef: RefObject<HTMLDivElement | null>;
};

export type TabsRef = {
  setCurrentTab: (tab: string | null) => void;
};

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("useTabsContext must be used within a TabsProvider");
  }
  return context;
}

function useTabs() {
  return useAtom(useTabsContext().tabsAtom);
}

export function useCurrentTab() {
  return useAtom(useTabsContext().currentTabAtom);
}
